import axios, { AxiosRequestConfig } from 'axios';
import { runtimeConfig, serverRuntimeConfig } from 'config/runtimeConfig';
import { ProfileType } from 'data/interfaces/IRegistration';
import Cookies from 'js-cookie';
import NodeCache from 'node-cache';
import queryString from 'query-string';
import { CookiesLifeTime } from 'types/constants';
import { CookiesType, InterfaceLanguage } from 'types/enums';
import { RequestError } from 'types/types';
import { isClient, isServer } from 'utils/media';
import { translate } from './translate';

interface Options {
  url: string;
  method?: 'post' | 'put' | 'get' | 'delete' | 'patch';
  data?: any;
  token?: string;
  sessionId?: string;
  file?: File;
  disableCache?: boolean;
  profile?: ProfileType;
  config?: AxiosRequestConfig;
  headers?: Record<string, string>;
  ignoreToken?: boolean;
}

export const nodeCache = new NodeCache({ stdTTL: 60 * 60 * runtimeConfig.CACHE_TIME_HOURS });

const createAxiosInstance = () => {
  const { HOST_INNER } = serverRuntimeConfig;
  const { HOST } = runtimeConfig;

  const pendingRequests = new Map();

  const api = axios.create({
    baseURL: HOST_INNER || HOST,
  });

  api.interceptors.request.use((config) => {
    const controller = new AbortController();
    config.signal = controller.signal;

    pendingRequests.set(config.url, controller);
    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        pendingRequests.forEach((controller) => {
          controller.abort();
        });
        pendingRequests.clear();
      }
      return Promise.reject(error);
    },
  );

  return api;
};

async function request<T = any>(options: string | Options): Promise<T> {
  const { CACHE_TIME_HOURS } = runtimeConfig;
  const optionsIsString = typeof options === 'string';
  const accessToken =
    !optionsIsString && options.token ? options.token : Cookies.get(CookiesType.accessToken);
  const profile =
    !optionsIsString && options.profile ? options.profile : Cookies.get(CookiesType.profileType);
  const sessionId =
    !optionsIsString && options.sessionId ? options.sessionId : Cookies.get(CookiesType.sessionId);
  let url = '';
  let method = 'get';
  let data: any = null;
  let file: File | null = null;
  let disableCache = false;
  let config: AxiosRequestConfig = {};
  let ignoreToken = false;
  let optionHeaders: Record<string, string> = {};
  if (optionsIsString) {
    url = options;
  } else {
    url = options.url;
    method = options.method ? options.method.toLowerCase() : 'get';
    data = options.data;
    file = options.file ?? null;
    disableCache = options.disableCache ?? false;
    config = options.config as any;
    optionHeaders = options.headers;
    ignoreToken = options.ignoreToken;
  }

  if (data && data.lang === InterfaceLanguage.Ukranian) {
    data.lang = 'uk';
  }

  const api = createAxiosInstance();

  const correctUrl = `${url}${method === 'get' && data ? `?${queryParams(data)}` : ''}`;

  const needCache = !disableCache && CACHE_TIME_HOURS > 0 && isServer && method === 'get';
  if (needCache) {
    const cachedData = nodeCache.get<T>(correctUrl);
    if (cachedData) {
      return cachedData;
    }
  }

  const mulipartFormData = typeof FormData == 'undefined' ? null : new FormData();
  if (file && mulipartFormData) {
    mulipartFormData.append('file', file);
    data = mulipartFormData;
  }

  const headers: Record<string, string> = {
    Authorization: accessToken && !ignoreToken ? `Bearer ${accessToken}` : '',
    'X-Session': sessionId ? sessionId : '',
    'profile-type': profile,
    ...optionHeaders,
  };

  if (!file && !(data instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const dataHandler = (() => {
    if (method !== 'get') {
      if (data instanceof FormData) {
        return data;
      } else if (typeof data === 'object') {
        return JSON.stringify(data);
      } else {
        return data;
      }
    } else {
      return undefined;
    }
  })();

  const requestConfig = {
    url: correctUrl,
    method,
    headers: headers,
    data: dataHandler,
    ...config,
    validateStatus: () => true,
  };

  const res = await api.request(requestConfig);

  const xSession = res.headers['x-session'];
  if (xSession) {
    Cookies.set(CookiesType.sessionId, xSession, { expires: CookiesLifeTime.accessToken });
  }
  if (res.status === 401 || res.status === 403) {
    Cookies.remove(CookiesType.accessToken);
    const fromLogout = JSON.parse(Cookies.get(CookiesType.fromLogout) || 'false');
    if (isClient && !process.env.DEV && !fromLogout) {
      window.location.replace('/');
    }
  }

  const jsonData = res.data;

  if (res.status === 200 || res.status === 201) {
    if (needCache) {
      nodeCache.set<any>(correctUrl, jsonData);
    }
    return jsonData;
  }

  if (res.status >= 400 && res.status < 500) {
    console.warn(
      `Request error. Code: ${res.status}. URL: ${correctUrl}.`,
      jsonData?.errors || res.statusText || translate('error'),
      'response status:',
      res.status,
    );
    throw new RequestError(
      jsonData?.errors || res.statusText || translate('error'),
      res.status ?? 500,
    );
  }
}

function queryParams(params: { [key: string]: any }) {
  return queryString.stringify(params);
}

export default request;

export function catchNotFoundError(err: any): void {
  if (err instanceof RequestError && err.isNotFoundError) {
    return;
  }
  throw err;
}
